/**
 * CON QUÉ SE COMPARÓ un parte cuando a quien lo hacía se le preguntó «¿es lo mismo?»
 * (Pablo, 27-09-2026): «Es otra cosa» a secas no decía de qué. Ahora la ficha dice
 * «no es lo que se avisó en la línea 18 hace 3 días (araña roja)». La plaga solo si
 * aquello estaba CLASIFICADO: un parte sin clasificar no tiene plaga que nombrar.
 *
 * La pregunta se hizo sobre un FOCO (`followup_focus_id`) o sobre un parte suelto
 * (`followup_session_id`, `followUpColumns`). Del foco: su línea, su plaga y el último
 * muestreo que tenía ANTES de este parte (es lo que se le enseñó).
 */
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface FollowUpReference {
  line: number | null;
  /** YYYY-MM-DD del parte con el que se comparó. */
  date: string | null;
  /** Solo si estaba clasificado. */
  pestName: string | null;
}

const lineFromNotes = (notes: string | null | undefined): number | null => {
  const m = /l[ií]nea:\s*(\d+)/i.exec(notes ?? "");
  return m ? Number(m[1]) : null;
};

export function useFollowUpReference(
  ref: { focusId: string | null; sessionId: string | null },
  current: { id: string; date: string | null } | null,
) {
  return useQuery({
    queryKey: ["followup-reference", ref.focusId, ref.sessionId, current?.id],
    enabled: !!current && !!(ref.focusId || ref.sessionId),
    staleTime: 60_000,
    queryFn: async (): Promise<FollowUpReference | null> => {
      if (ref.focusId) {
        const [foco, ultimo] = await Promise.all([
          supabase.from("pest_foci").select("line_number, pest_name_snapshot").eq("id", ref.focusId).maybeSingle(),
          (() => {
            let q = supabase
              .from("pest_scouting_sessions")
              .select("date, notes, block_row:block_rows(line_number)")
              .eq("focus_id", ref.focusId!)
              .neq("id", current!.id)
              .or("triage_status.eq.classified,and(triage_status.eq.dismissed,dismissal_reason.eq.clean_check)")
              .order("date", { ascending: false })
              .order("created_at", { ascending: false })
              .limit(1);
            if (current!.date) q = q.lte("date", current!.date);
            return q;
          })(),
        ]);
        const f = foco.data as { line_number: number | null; pest_name_snapshot: string | null } | null;
        if (!f) return null;
        const u = (ultimo.data?.[0] ?? null) as { date: string; notes: string | null; block_row: { line_number: number } | null } | null;
        return {
          line: f.line_number ?? u?.block_row?.line_number ?? lineFromNotes(u?.notes),
          date: u?.date ?? null,
          pestName: f.pest_name_snapshot ?? null,
        };
      }
      const { data } = await supabase
        .from("pest_scouting_sessions")
        .select("date, notes, triage_status, triage_pest_name_snapshot, block_row:block_rows(line_number)")
        .eq("id", ref.sessionId!)
        .maybeSingle();
      const s = data as { date: string; notes: string | null; triage_status: string | null; triage_pest_name_snapshot: string | null; block_row: { line_number: number } | null } | null;
      if (!s) return null;
      return {
        line: s.block_row?.line_number ?? lineFromNotes(s.notes),
        date: s.date,
        pestName: s.triage_status === "classified" ? s.triage_pest_name_snapshot : null,
      };
    },
  });
}
